<template>
  <Head :title="__('contact.meta.title')">
    <meta
      name="description"
      :content="__('contact.meta.description')"
    >
    <link
      rel="canonical"
      :href="localeRoute('frontend.contact')"
    >
    <meta
      property="og:image"
      content="/frontend/assets/favicon/og-image.jpg"
    >
  </Head>

  <section class="contact-hero py-20 relative overflow-hidden">
    <img
      src="/frontend/assets/mobile-hero-swirl.svg"
      class="absolute top-0 left-0 lg:hidden"
    >
    <img
      src="/frontend/assets/desktop-hero-swirl.svg"
      class="absolute top-0 left-0 hidden lg:block"
    >

    <div class="container">
      <div class="text-center px-8">
        <h1 class="text-5xl font-bold lg:text-7xl">
          {{ __('contact.title') }}
        </h1>

        <p class="text-gray-400 mt-2 lg:text-xl mb-0 lg:w-3/5 lg:mx-auto">
          {{ __('contact.text') }}
        </p>
      </div>
    </div>
  </section>

  <section class="contact py-10 md:py-20 bg-white overflow-hidden relative">
    <img
      src="/frontend/assets/scribble-round-desktop.svg"
      class="hidden lg:block absolute left-0 top-[500px]"
    >

    <div class="container">
      <div class="flex flex-wrap">
        <div class="w-full md:w-7/12">
          <form @submit.prevent="submit">
            <div class="flex flex-wrap relative">
              <div class="w-full mb-4 md:w-1/2 md:pr-2">
                <InputField
                  v-model="form.name"
                  :form="form"
                  name="name"
                  type="text"
                  :label="__('contact.form.name.label')"
                  :placeholder="__('contact.form.name.placeholder')"
                />
              </div>

              <div class="w-full mb-4 md:w-1/2 md:pl-2">
                <InputField
                  v-model="form.email"
                  :form="form"
                  name="email"
                  type="email"
                  :label="__('contact.form.email.label')"
                  :placeholder="__('contact.form.email.placeholder')"
                />
              </div>

              <div class="w-full mb-4">
                <InputField
                  v-model="form.phone"
                  :form="form"
                  name="phone"
                  type="tel"
                  :label="__('contact.form.phone.label')"
                  :placeholder="__('contact.form.phone.placeholder')"
                  :is-required="false"
                />
              </div>

              <div class="w-full mb-4">
                <TextareaField
                  v-model="form.message"
                  :form="form"
                  name="message"
                  :label="__('contact.form.message.label')"
                  :placeholder="__('contact.form.message.placeholder')"
                  rows="8"
                />
              </div>
              <div class="mb-4">
                <VueRecaptcha
                  ref="recaptcha"
                  :sitekey="recaptchaSiteKey"
                  @verify="(response) => form.recaptcha = response"
                  @expired="() => form.recaptcha = ''"
                />

                <p
                  v-if="form.errors.recaptcha"
                  class="text-red text-xs italic"
                >
                  {{ __('contact.form.recaptcha-invalid') }}
                </p>
              </div>
              <div class="w-full text-center">
                <button
                  type="submit"
                  class="button-black shadow-button-black !text-base w-auto inline-block !px-16 mx-auto"
                >
                  <span v-if="form.processing">
                    <img
                      src="/frontend/assets/spinner.svg"
                      class="h-6 w-6 animate-spin"
                    >
                  </span>
                  <span v-else-if="form.wasSuccessful">
                    Bedankt voor uw bericht!
                  </span>
                  <span v-else>
                    {{ __('contact.form.send') }}
                  </span>
                </button>
              </div>

              <img
                src="/frontend/assets/scribble-round-desktop.svg"
                class="absolute -right-[45px] -bottom-[60px] md:hidden"
              >
            </div>
          </form>
        </div>

        <div class="w-full text-gray-400 text-lg mt-12 md:w-5/12 md:pl-8 md:mt-0">
          <p>
            <b>{{ __('contact.contact.title') }}</b><br>
            {{ __('contact.contact.street') }}<br>
            {{ __('contact.contact.city') }}<br>
            {{ __('contact.contact.country') }}<br>
          </p>

          <div class="grid grid-cols-1 lg:grid-cols-2 lg:gap-x-8 lg:gap-y-4 md:mt-10 relative">
            <img
              src="/frontend/assets/scribble-top-desktop-black.svg"
              class="hidden lg:block absolute -right-[60px] -top-[80px]"
            >

            <p>
              <b>{{ __('contact.support.title') }}</b> <br>
              <a
                :href="`mailto:${__('contact.support.email')}`"
                target="_blank"
                class="no-underline hover:underline"
              >
                {{ __('contact.support.email') }}
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import {useForm} from '@inertiajs/vue3';
import { VueRecaptcha } from 'vue-recaptcha';
import InputField from '../../Components/Form/InputField.vue';
import TextareaField from '../../Components/Form/TextareaField.vue';

export default {
  components: {
    VueRecaptcha,
    InputField,
    TextareaField,
  },

  props: {
    recaptchaSiteKey: {
      type: String,
      required: true,
    },
  },

  setup() {
    const form = useForm({
      name: '',
      email: '',
      phone: '',
      message: '',
      recaptcha: '',
    });

    return {form};
  },

  methods: {
    submit() {
      if (this.form.processing) {
        return;
      }

      this.form.post(this.localeRoute('frontend.contact.store'), {
        onSuccess: () => this.form.reset(),
        onError: () => {
          // Reset reCAPTCHA on validation error
          this.form.recaptcha = '';
          if (this.$refs.recaptcha) {
            this.$refs.recaptcha.reset();
          }
        },
      });
    },
  },
};
</script>
